import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, Megaphone, Check, Plus } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { InlineSpinner } from '@/components/ui/inline-spinner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@signalvote/api-client';
import type { TenantCampaignWithCapabilitiesDto, PresetKey } from '@signalvote/shared';
import { useCampaignStore } from '@/stores';
import { myCampaignsQuery } from '@/hooks/queries';
import { cn } from '@/lib/utils';

type BadgeColor = 'primary' | 'secondary' | 'default';

const presetColors: Record<string, BadgeColor> = {
  FullAdmin: 'primary',
  FieldDirector: 'secondary',
  Volunteer: 'default',
  ViewOnly: 'default',
};

const badgeVariantForColor: Record<BadgeColor, 'default' | 'secondary' | 'outline'> = {
  primary: 'default',
  secondary: 'secondary',
  default: 'outline',
};

interface CampaignSelectorProps {
  onCreateCampaign?: () => void;
  compact?: boolean;
  /** Use dark mode styling for dark backgrounds */
  darkMode?: boolean;
}

export const CampaignSelector: React.FC<CampaignSelectorProps> = ({
  onCreateCampaign,
  compact = false,
  darkMode = false,
}) => {
  const [isReloading, setIsReloading] = useState(false);
  const queryClient = useQueryClient();
  const api = getApiClient();
  const autoSelectAttempted = useRef(false);

  const {
    selectedCampaign,
    selectCampaign,
  } = useCampaignStore();

  // Fetch user's campaigns
  const { data: campaigns = [], isLoading } = useQuery(myCampaignsQuery());

  // Switch campaign mutation
  const { mutate: switchCampaign, mutateAsync: switchCampaignAsync, isPending: isSwitching } = useMutation({
    mutationFn: async (campaignId: string) => {
      return api.tenantCampaigns.switchCampaign({ campaignId });
    },
    onSuccess: (data) => {
      selectCampaign(data.campaign);
      // Show loading overlay, then reload for clean state
      setIsReloading(true);
      setTimeout(() => window.location.reload(), 400);
    },
  });

  // Auto-select campaign if user has exactly one and none is selected
  useEffect(() => {
    if (
      !isLoading &&
      campaigns.length === 1 &&
      !selectedCampaign &&
      !autoSelectAttempted.current
    ) {
      autoSelectAttempted.current = true;
      // Auto-switch to the only available campaign
      switchCampaign(campaigns[0].id);
    }
  }, [campaigns, isLoading, selectedCampaign, switchCampaign]);

  const handleSelectCampaign = async (campaign: TenantCampaignWithCapabilitiesDto) => {
    if (campaign.id === selectedCampaign?.id) {
      return;
    }

    await switchCampaignAsync(campaign.id);
  };

  const handleCreateCampaign = () => {
    onCreateCampaign?.();
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <InlineSpinner />
        {!compact && <span className="text-sm">Loading...</span>}
      </div>
    );
  }

  const badgeLabel = (campaign: TenantCampaignWithCapabilitiesDto) =>
    campaign.isOwner ? 'Owner' : (campaign.presetKey ?? 'Custom');

  const badgeColor = (campaign: TenantCampaignWithCapabilitiesDto): BadgeColor =>
    campaign.isOwner ? 'primary' : presetColors[campaign.presetKey ?? ''] ?? 'default';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={isSwitching}
            className={cn(
              'inline-flex items-center justify-between gap-2 rounded-md border text-sm font-medium normal-case transition-colors disabled:pointer-events-none disabled:opacity-50',
              compact ? 'h-8 px-3 text-xs' : 'h-9 min-w-0 px-3 py-2 md:min-w-[200px] md:px-4',
              !darkMode && 'border-input bg-background hover:bg-accent hover:text-accent-foreground'
            )}
            style={
              darkMode
                ? {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'rgba(255, 255, 255, 0.9)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                : undefined
            }
          >
            <span className="inline-flex min-w-0 items-center gap-2">
              <Megaphone className="h-4 w-4 shrink-0" />
              {isSwitching ? (
                <InlineSpinner />
              ) : selectedCampaign ? (
                <span className="inline-flex min-w-0 items-center gap-2">
                  <span
                    className={cn(
                      'overflow-hidden text-ellipsis whitespace-nowrap',
                      compact ? 'max-w-[100px]' : 'max-w-[150px]'
                    )}
                  >
                    {selectedCampaign.name}
                  </span>
                  {!compact && (
                    <Badge
                      variant={badgeVariantForColor[badgeColor(selectedCampaign)]}
                      className="h-5 shrink-0 px-1.5 text-[0.7rem] font-semibold"
                      style={
                        darkMode
                          ? { backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white' }
                          : undefined
                      }
                    >
                      {badgeLabel(selectedCampaign)}
                    </Badge>
                  )}
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">Select Campaign</span>
              )}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="min-w-[250px] max-h-[400px]">
          {campaigns.length === 0 ? (
            <DropdownMenuItem disabled>
              <span className="text-sm text-muted-foreground">No campaigns available</span>
            </DropdownMenuItem>
          ) : (
            campaigns.map((campaign) => (
              <DropdownMenuItem
                key={campaign.id}
                onClick={() => handleSelectCampaign(campaign)}
                className={cn(campaign.id === selectedCampaign?.id && 'bg-accent')}
              >
                {campaign.id === selectedCampaign?.id ? (
                  <Check className="h-4 w-4 text-primary" />
                ) : (
                  <Megaphone className="h-4 w-4" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap">
                    {campaign.name}
                  </span>
                  {campaign.description && (
                    <span className="block max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap text-xs text-muted-foreground">
                      {campaign.description}
                    </span>
                  )}
                </span>
                <Badge
                  variant={badgeVariantForColor[badgeColor(campaign)]}
                  className="ml-1 h-5 px-1.5 text-[0.7rem] font-semibold"
                >
                  {badgeLabel(campaign)}
                </Badge>
              </DropdownMenuItem>
            ))
          )}

          {onCreateCampaign && [
            <DropdownMenuSeparator key="create-divider" />,
            <DropdownMenuItem key="create-campaign" onClick={handleCreateCampaign}>
              <Plus className="h-4 w-4" />
              <span>Create New Campaign</span>
            </DropdownMenuItem>,
          ]}
        </DropdownMenuContent>
      </DropdownMenu>

      {isReloading &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-4 bg-background">
            <InlineSpinner className="h-10 w-10" />
            <span className="text-sm text-muted-foreground">Switching campaign...</span>
          </div>,
          document.body
        )}
    </>
  );
};

export default CampaignSelector;
